"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

export interface CheckboxVisualProps {
  checked?: boolean | "indeterminate";
  disabled?: boolean;
  className?: string;
}

export interface CheckboxProps {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
}

/**
 * Visual checkbox component - renders just the checkbox box with styling
 * Can be used standalone with a wrapper label or integrated into other components
 */
export const CheckboxVisual = React.forwardRef<HTMLDivElement, CheckboxVisualProps>(
  ({ checked, disabled, className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "size-4 rounded-sm border-2 flex items-center justify-center transition-all duration-200",
          checked === true || checked === "indeterminate"
            ? "bg-cede-primary border-cede-primary"
            : "border-cede-text-inactive bg-transparent hover:border-cede-text-secondary",
          disabled && "opacity-50 cursor-not-allowed",
          className,
        )}
      >
        {checked === true && <Check className="size-3 text-white" />}
        {checked === "indeterminate" && <div className="w-2 h-0.5 bg-white" />}
      </div>
    );
  },
);

CheckboxVisual.displayName = "CheckboxVisual";

/**
 * Complete checkbox component with label wrapper and input
 * Use this for standalone checkboxes outside of other component systems
 */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ checked, onCheckedChange, disabled, className, id }, ref) => {
    return (
      <label
        htmlFor={id}
        className={cn(
          "inline-flex items-center cursor-pointer group",
          // Show the focus ring only on keyboard focus (:focus-visible) so a
          // plain mouse click on the label doesn't leave a lingering outline.
          "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-cede-primary has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-cede-bg-primary",
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
      >
        <input
          ref={ref}
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheckedChange?.(e.target.checked)}
          disabled={disabled}
          className="sr-only peer"
        />
        <CheckboxVisual checked={checked} disabled={disabled} />
      </label>
    );
  },
);

Checkbox.displayName = "Checkbox";
